import React from 'react';
import PropTypes from 'prop-types';
import { CaretRightIcon, CaretDownIcon } from '@phosphor-icons/react';

function escapeForRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Splits the name on the search term (case-insensitive) and wraps each match in
// <strong>. Returns the name unhighlighted when the search term is empty so the
// highlight never shows up unprompted.
//
// Every piece of text sits in its own element: page translators such as Google
// Translate swap bare text nodes for <font> elements, and React then fails to
// remove or insert beside the nodes it rendered as the highlight changes.
function highlightMatch(name, searchTerm) {
  if (!searchTerm) return <span>{name}</span>;

  const parts = name.split(new RegExp(`(${escapeForRegex(searchTerm)})`, 'i'));
  return parts.map((part, index) => {
    if (part === '') return null;
    return index % 2 === 1 ? <strong key={index}>{part}</strong> : <span key={index}>{part}</span>;
  });
}

const ResultRow = ({
  item,
  // Unique per visible row. The same institution can appear under more than one
  // parent (multi-rooted GRIDs), so keying the checkbox id on item.id alone would
  // emit duplicate DOM ids and mis-wire the label→input association across trees.
  rowId = item.id,
  searchTerm,
  isSelected,
  isFocused,
  disabled,
  lockedByParent = false,
  onToggle,
  onFocus = null,
  renderMeta = null,
  renderExtras = null,
  treeMode = false,
  isParent = false,
  isChild = false,
  expanded = false,
  onToggleExpand = null,
  ariaSetSize,
  ariaPosInSet,
  parentCheckboxState = 'unchecked'
}) => {
  const role = treeMode ? 'treeitem' : 'option';
  const level = isChild ? 2 : 1;
  const effectivelyChecked = lockedByParent || (isParent ? parentCheckboxState === 'checked' : isSelected);
  // aria-selected is boolean-only; a parent's partial (mixed) state is conveyed
  // by the checkbox's indeterminate property, so the row itself reads unselected.
  const ariaSelected = (isParent ? parentCheckboxState === 'checked' : effectivelyChecked) ? 'true' : 'false';

  const className = ['ModalPicker-result']
    .concat(isParent ? ['ModalPicker-result--parent'] : [])
    .concat(isChild ? ['ModalPicker-result--child'] : [])
    .join(' ');

  const checkboxRef = (node) => {
    if (!node) return;
    if (isParent) {
      node.indeterminate = parentCheckboxState === 'mixed';
    }
    if (isFocused) node.focus();
  };

  return (
    <div
      role={role}
      aria-selected={ariaSelected}
      aria-level={treeMode ? level : undefined}
      aria-expanded={isParent ? expanded : undefined}
      aria-setsize={treeMode ? ariaSetSize : undefined}
      aria-posinset={treeMode ? ariaPosInSet : undefined}
      className={className}
    >
      <label htmlFor={`modal-picker-result-${rowId}`} className="ModalPicker-resultLabel">
        <input
          ref={checkboxRef}
          type="checkbox"
          id={`modal-picker-result-${rowId}`}
          className="ModalPicker-checkbox"
          checked={effectivelyChecked}
          // A child locked by its parent reads as disabled but stays in the tab
          // order: native `disabled` removes it from keyboard focus, which would
          // strand arrow-key navigation when it lands on a checked branch. The
          // onChange guard below keeps it non-toggleable. The max-selected cap
          // genuinely disables a row, so that still uses native `disabled`.
          disabled={disabled}
          aria-disabled={lockedByParent || undefined}
          onChange={(event) => {
            if (lockedByParent) return;
            onToggle(item, event.target.checked);
          }}
          // Mouse-click and Tab focus this checkbox without going through the
          // arrow-key handler, so report focus up to keep the roving navigation
          // index aligned with the row the user is actually on.
          onFocus={onFocus ?? undefined}
          tabIndex={isFocused ? '0' : '-1'}
        />
        {isParent && (
          <button
            type="button"
            className="ModalPicker-chevron"
            aria-hidden="true"
            tabIndex={-1}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onToggleExpand?.(item);
            }}
          >
            {expanded ? <CaretDownIcon size="16" weight="bold" /> : <CaretRightIcon size="16" weight="bold" />}
          </button>
        )}
        <span className="ModalPicker-resultText">
          <span className="ModalPicker-resultName">
            {highlightMatch(item.name, searchTerm)}
            {isParent && <span> (all)</span>}
          </span>
          {renderMeta && renderMeta(item)}
        </span>
      </label>
      {renderExtras && renderExtras(item)}
    </div>
  );
};

ResultRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  rowId: PropTypes.string,
  searchTerm: PropTypes.string,
  isSelected: PropTypes.bool,
  isFocused: PropTypes.bool,
  disabled: PropTypes.bool,
  lockedByParent: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  renderMeta: PropTypes.func,
  renderExtras: PropTypes.func,
  treeMode: PropTypes.bool,
  isParent: PropTypes.bool,
  isChild: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  ariaSetSize: PropTypes.number,
  ariaPosInSet: PropTypes.number,
  parentCheckboxState: PropTypes.oneOf(['unchecked', 'mixed', 'checked'])
};

export default ResultRow;
